/**
 * #147 · La clave de descifrado de una orden, para entregársela al impresor.
 *
 * Este panel **sólo existe para el personal de XYVONA con el permiso
 * `print_file:reveal_key` otorgado**. No es un rol: un administrador de una
 * marca no lo ve, y un administrador de XYVONA tampoco hasta que alguien se lo
 * otorgue desde Herramientas. Ver `services/api/permissions.py`.
 *
 * Tres decisiones de esta pantalla, y ninguna es estética:
 *
 * 1. **La clave no se pide sola.** Hay que apretar un botón y el pedido queda
 *    auditado. Que aparezca al abrir la orden la dejaría en la pantalla de
 *    cualquiera que pase por el escritorio, y en una captura de pantalla.
 * 2. **No se muestra junto al enlace de descarga.** Están en dos paneles
 *    distintos a propósito: la separación de canales la sostiene la persona
 *    que despacha, y una pantalla que las junta invita a mandarlas juntas.
 * 3. **Se puede ocultar de nuevo.** Después de copiarla no hay motivo para
 *    que siga a la vista.
 */
import { useMutation } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';

import type { Schemas } from '@/api/types';
import { useApi } from '@/api/useApi';
import { useI18n } from '@/i18n/useI18n';
import { Button } from '@/ui/Button';
import { ErrorState } from '@/ui/states';

type OrderKey = Schemas['PrintFileKeyOut'];

export function OrderKeyPanel({ orderId }: { orderId: number }): ReactNode {
  const { t } = useI18n();
  const api = useApi();
  const [copiada, setCopiada] = useState(false);

  // Mutación y no query: pedir la clave es un ACTO que se audita, no una
  // lectura que se puede repetir sola. Con `useQuery`, un refetch de fondo
  // —cambiar de pestaña y volver— escribiría entradas de auditoría que nadie
  // pidió, y el registro dejaría de decir quién la miró y cuándo.
  const clave = useMutation<OrderKey>({
    mutationFn: () => api.get<OrderKey>(`/api/v1/label-orders/${String(orderId)}/print-file/key`),
  });

  const ocultar = (): void => {
    clave.reset();
    setCopiada(false);
  };

  if (clave.isError) {
    return (
      <div className="space-y-3">
        <ErrorState error={clave.error} onRetry={ocultar} />
      </div>
    );
  }

  if (clave.data === undefined) {
    return (
      <div className="space-y-2">
        <Button
          variant="secondary"
          disabled={clave.isPending}
          onClick={() => {
            clave.mutate();
          }}
        >
          {clave.isPending ? t('labels.orderKey.working') : t('labels.orderKey.request')}
        </Button>
        <p className="text-xs text-ink-subtle">{t('labels.orderKey.request.hint')}</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div
        role="note"
        className="rounded-(--radius-card) border border-suspect/30 bg-suspect-soft p-4"
      >
        <p className="text-sm font-semibold text-suspect">{t('labels.orderKey.warning.title')}</p>
        <p className="mt-1 text-sm text-ink">{t('labels.orderKey.warning.detail')}</p>
      </div>

      <div>
        <p className="text-xs tracking-wide text-ink-subtle uppercase">
          {t('labels.orderKey.value', { order: clave.data.order_code })}
        </p>
        <code className="mt-0.5 block font-mono text-sm break-all text-ink select-all">
          {clave.data.key}
        </code>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <Button
          size="sm"
          onClick={() => {
            void navigator.clipboard.writeText(clave.data.key).then(() => {
              setCopiada(true);
            });
          }}
        >
          {copiada ? t('labels.orderKey.copied') : t('labels.orderKey.copy')}
        </Button>
        <Button size="sm" variant="ghost" onClick={ocultar}>
          {t('labels.orderKey.hide')}
        </Button>
      </div>

      <p className="text-xs text-ink-subtle">{t('labels.orderKey.audited')}</p>
    </div>
  );
}
